import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import * as MuiIcons from '@mui/icons-material';
import AddSymbolButton from './AddSymbolButton';
import { useCategories, useFavoriteLists, toggleFavoriteList, useListPerformance, useWatchlistLabels, useSignalWatches } from '../services/api';
import { isStrategyTab, findStrategyForTab, strategyTabId, strategyCaption } from '../utils/strategyTabs';
import { flattenFilters } from '../utils/listUrl';
import LabelManagerModal from './LabelManagerModal';
import { useQueryClient } from '@tanstack/react-query';
import { usePopper } from 'react-popper';
import SettingsDialog from './SettingsDialog';
import { FIELD_NAMES, DEFAULT_COLOR_SETTINGS } from '../config/constants';
import FilterHeaderIcons from './FilterHeaderIcons';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import AIFilterModal from './AIFilterModal';
import CustomFilterModal from './CustomFilterModal';
import SubscriptionIndicator from './SubscriptionIndicator';
import journeyTracker from '../utils/journeyTracker';
import {
  useAIPortfolios, useAIPortfolioHoldings,
  usePowerUserCheck, usePublicPortfolios, useFollowedPortfolios,
  useFollowPortfolio, useUnfollowPortfolio, useDismissPortfolio,
} from '../services/api';
import AIPortfolioCreateModal from './AIPortfolioCreateModal';

const PlBadge = ({ value, prefix }) => {
  if (value == null) return null;
  const isPositive = value > 0;
  const isNegative = value < 0;
  const text = `${prefix ? prefix + ': ' : ''}${isPositive ? '+' : ''}${value.toFixed(1)}%`;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '1px 6px', marginLeft: '4px',
      fontSize: '11px', fontWeight: 600, borderRadius: '4px', whiteSpace: 'nowrap',
      ...(isPositive && { backgroundColor: '#dcfce7', color: '#15803d' }),
      ...(isNegative && { backgroundColor: '#fee2e2', color: '#dc2626' }),
      ...(!isPositive && !isNegative && { backgroundColor: '#f3f4f6', color: '#6b7280' }),
    }}>
      {text}
    </span>
  );
};

const PortfolioTodayBadge = ({ portfolioId }) => {
  const { data } = useAIPortfolioHoldings(portfolioId);
  const totalPl = data?.summary?.total_pl_pct;
  const todayPl = data?.summary?.today_pl_pct;
  return (
    <>
      <PlBadge value={todayPl} prefix="1D" />
      <PlBadge value={totalPl} prefix="All" />
    </>
  );
};

const getPortfolioTradesToday = (portfolio) => {
  if (!portfolio.last_rebalanced_at) return 0;
  const today = new Date().toLocaleDateString('en-CA');
  const rebalanceDate = new Date(portfolio.last_rebalanced_at).toLocaleDateString('en-CA');
  if (rebalanceDate !== today) return 0;
  try {
    const summary = typeof portfolio.last_ai_summary === 'string'
      ? JSON.parse(portfolio.last_ai_summary)
      : portfolio.last_ai_summary;
    return summary?.lastRebalance?.tradesProposed || 0;
  } catch { return 0; }
};

// The views of the smart-list menu, in switch order. 'sector' is only offered once the
// sector categories exist (the smart_list_cron job that builds them has run).
const LIST_DIMENSIONS = [
  { id: 'timeframe', label: 'Horizon', title: 'Group lists by holding horizon (day, swing, position, long-term)' },
  { id: 'analysis', label: 'Strategy', title: 'Group lists by strategy (technical, fundamental, events, themes...)' },
  { id: 'sector', label: 'Sector', title: 'Group lists by sector and industry' },
];

const readListDimension = () => {
  const saved = localStorage.getItem('listDimension');
  return LIST_DIMENSIONS.some(({ id }) => id === saved) ? saved : 'timeframe';
};

const useClickOutside = (ref, popperRef, handler) => {
  useEffect(() => {
    const listener = (event) => {
      const isInsideContainer = ref.current && ref.current.contains(event.target);
      const isInsidePopper = popperRef.current && popperRef.current.contains(event.target);
      
      if (isInsideContainer || isInsidePopper) {
        return;
      }
      
      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, popperRef, handler]);
};

const FilterDropdowns = ({
  selectedTab,
  onSelectTab,
  onAddSymbol,
  loggedInUser,
  existingSymbols,
  activeFilters,
  onFilterApply,
  watchlist,
  userSettings,
  onModalOpen,
  onModalContentSet,
  onSuccess,
  onDeleteList,
  userListsData
}) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [activeSettingsCategory, setActiveSettingsCategory] = useState(null);
  const [colorSettings, setColorSettings] = useState(() => {
    const savedSettings = localStorage.getItem('colorSettings');
    return savedSettings ? JSON.parse(savedSettings) : DEFAULT_COLOR_SETTINGS;
  });
  const { data: categories, isLoading, error } = useCategories();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeDimension, setActiveDimension] = useState(readListDimension);

  // Fetch user's favorite lists
  const { data: favoriteLists = [] } = useFavoriteLists(loggedInUser?.email);

  // Build favorited smart lists from categories data (deduplicated by id)
  const favoritedSmartLists = useMemo(() => {
    if (!favoriteLists.length || !categories) return [];

    const seenIds = new Set();
    const allSmartLists = [];
    categories.forEach(cat => {
      if (cat.dimension !== 'personal' && cat.filters) {
        // nested lists (a filter's `children`) can be favorited too
        flattenFilters(cat.filters).forEach(({ filter }) => {
          if (favoriteLists.includes(filter.id) && !seenIds.has(filter.id)) {
            seenIds.add(filter.id);
            allSmartLists.push({
              ...filter,
              type: 'favorite_smart_list',
              sourceCategory: cat.name
            });
          }
        });
      }
    });
    return allSmartLists;
  }, [favoriteLists, categories]);

  // Save dimension preference to localStorage
  const handleDimensionSelect = (dimension) => {
    setActiveDimension(dimension);
    localStorage.setItem('listDimension', dimension);
  };

  // No sector categories yet (the cron hasn't run): hide the Sector option, and show Timeframe
  // in place of a saved 'sector' without overwriting it, so the choice returns once they exist.
  const hasSectorCategories = !!categories?.some(cat => cat.dimension === 'sector');
  const dimensionOptions = LIST_DIMENSIONS.filter(({ id }) => id !== 'sector' || hasSectorCategories);
  const shownDimension = dimensionOptions.some(({ id }) => id === activeDimension) ? activeDimension : 'timeframe';

  // Filter categories by active dimension (always show 'personal')
  const filteredCategories = categories?.filter(
    cat => cat.dimension === 'personal' || cat.dimension === shownDimension
  );

  console.log('FilterDropdowns received userListsData:', userListsData);
  const userLists = userListsData || [];
  console.log('Processed userLists:', userLists);

  // Add logging to debug any potential duplicates
  if (userLists.length > 0) {
    const nameOccurrences = {};
    userLists.forEach(list => {
      if (!nameOccurrences[list.name]) {
        nameOccurrences[list.name] = 1;
      } else {
        nameOccurrences[list.name]++;
      }
    });
    
    // Log any duplicates found
    const duplicates = Object.entries(nameOccurrences)
      .filter(([_, count]) => count > 1)
      .map(([name]) => name);
      
    if (duplicates.length > 0) {
      console.log('Found duplicate lists with names:', duplicates);
      // Log details about the duplicates
      duplicates.forEach(name => {
        const matchingLists = userLists.filter(list => list.name === name);
        console.log(`Details for duplicates of "${name}":`, matchingLists.map(list => ({
          id: list.id, 
          type: list.type, 
          symbolsCount: Array.isArray(list.symbols) ? list.symbols.length : typeof list.symbols
        })));
      });
    }
  }

  const handleSettingsClick = (categoryId) => {
    setActiveSettingsCategory(categoryId);
    setIsSettingsOpen(true);
  };

  const handleSettingsSave = (newSettings) => {
    const validSettings = newSettings.filter(setting => 
      setting.conditions.every(condition => FIELD_NAMES.includes(condition.field))
    );
    setColorSettings(validSettings);
    localStorage.setItem('colorSettings', JSON.stringify(validSettings));
    setIsSettingsOpen(false);
  };

  const handleDropdownToggle = (categoryId) => {
    setActiveDropdown(current => current === categoryId ? null : categoryId);
  };

  const handleFilterClick = (filterId, event) => {
    if (filterId === 'ai_filter') {
      journeyTracker.trackAction('ai_list_generate');
      onModalContentSet({
        content: <AIFilterModal
          referenceElement={event.currentTarget}
          onClose={() => onModalOpen(false)}
          onSuccess={onSuccess}
        />
      });
      onModalOpen(true);
    } else {
      if (selectedTab !== filterId) {
        // Track list selection
        const listName = filterId; // The filter ID is typically the list name
        journeyTracker.trackListSelect(filterId, listName);
        onSelectTab(filterId);
      }
      handleDropdownToggle(null);
    }
  };

  const isListInCategory = useCallback((list, categoryId) => {
    if (list.categories) {
      return list.categories.includes(categoryId);
    }
    return list.category_id === categoryId;
  }, []);

  if (isLoading) {
    return (
      <div className="flex gap-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse h-10 w-32 bg-gray-200 rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    console.error('Error loading categories:', error);
    return <div className="text-red-500">Failed to load categories</div>;
  }

  return (
    <>
      <div className="flex justify-between items-center w-full mb-4">
        <div className="flex flex-wrap items-center gap-2">
          {filteredCategories?.map((category) => (
            <div key={category.id} className="flex items-center">
              <FilterDropdown
                category={category}
                selectedTab={selectedTab}
                onSelectTab={onSelectTab}
                onAddSymbol={onAddSymbol}
                loggedInUser={loggedInUser}
                existingSymbols={existingSymbols}
                isOpen={activeDropdown === category.id}
                onToggle={handleDropdownToggle}
                watchlist={watchlist}
                isListInCategory={isListInCategory}
                userLists={userLists}
                favoritedSmartLists={category.id === 'personal' ? favoritedSmartLists : []}
                onModalOpen={onModalOpen}
                onModalContentSet={onModalContentSet}
                onSuccess={onSuccess}
                onDeleteList={onDeleteList}
              />
              {category.id === 'personal' && (
                <>
                  <AddSymbolButton
                    onAddSymbol={onAddSymbol}
                    existingSymbols={existingSymbols}
                    isLoggedIn={!!loggedInUser?.email}
                    className="ml-2 p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none"
                    data-tooltip="Add symbol to Watch List"
                    aria-label="Add symbol to Watch List"
                    usePortal={true}
                  >
                    +
                  </AddSymbolButton>
                </>
              )}
            </div>
          ))}
          {/* Dimension switch; label and pill share a wrapper so the bar never wraps between them */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
          <span
            className="text-xs"
            // text-gray-500 is missing from the prebuilt tailwind-output.css
            style={{ color: '#6b7280', whiteSpace: 'nowrap' }}
          >
            Group by
          </span>
          <div
            role="group"
            aria-label="Group by"
            className="flex items-center rounded-full bg-gray-100"
            // p-0.5 / whitespace-nowrap / shadow-sm / text-gray-800 / font-medium are missing from the prebuilt
            // tailwind-output.css, so those bits are inline styles
            style={{ padding: 2, whiteSpace: 'nowrap' }}
          >
            {dimensionOptions.map(({ id, label, title }) => (
              <button
                key={id}
                type="button"
                onClick={() => handleDimensionSelect(id)}
                aria-pressed={shownDimension === id}
                title={title}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  shownDimension === id ? 'bg-white' : 'text-gray-600 hover:bg-gray-200'
                }`}
                style={shownDimension === id
                  ? { fontWeight: 500, color: '#1f2937', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }
                  : { fontWeight: 500 }}
              >
                {label}
              </button>
            ))}
          </div>
          </div>
          <Link
            to="/lists"
            className="bg-gray-100 hover:bg-gray-200"
            style={{
              marginLeft: 8, padding: '6px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 500,
              color: '#4b5563', textDecoration: 'none', whiteSpace: 'nowrap',
            }}
            title="Every list has a URL you can bookmark or share"
          >
            Browse all lists →
          </Link>
        </div>

        <div className="flex items-center ml-auto">
          <FilterHeaderIcons
            activeFilters={activeFilters}
            onApplyFilters={onFilterApply}
            onFilterClick={() => setIsFilterOpen(true)}
          />
        </div>
      </div>

      <SettingsDialog
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onSave={handleSettingsSave}
        initialSettings={colorSettings}
      />

      <Tooltip 
        id="tooltip" 
        place="right" 
        offset={5}
        className="z-[9999]"
      />
    </>
  );
};

// Exported for the /lists index, so its rows show the same icons as the picker.
export const IconComponent = ({ iconName }) => {
  // Handle null/undefined iconName
  if (!iconName) {
    return (
      <span className="category-icon">
        <MuiIcons.LightbulbOutlined />
      </span>
    );
  }

  const cleanIconName = iconName.startsWith('fa-') ? iconName.slice(3) : iconName;

  // Special cases mapping for AI-related icons
  const specialIcons = {
    'magic': 'AutoAwesome',
    'trash': 'Delete'
  };

  // Use special mapping if it exists, otherwise use the standard formatting
  const formattedName = specialIcons[cleanIconName] || cleanIconName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

  const IconElement = MuiIcons[formattedName];

  return (
    <span className="category-icon">
      {IconElement ? <IconElement /> : <MuiIcons.HelpOutline />}
    </span>
  );
};

// Performance badge component for lists
const PerformanceBadge = ({ listId, performanceData }) => {
  const perf = performanceData?.[listId];

  if (!perf || perf.avgChange == null) return null;

  const isPositive = perf.avgChange > 0;
  const isNegative = perf.avgChange < 0;
  const changeText = `${isPositive ? '+' : ''}${perf.avgChange.toFixed(1)}%`;

  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '1px 6px',
    marginLeft: '8px',
    fontSize: '11px',
    fontWeight: 600,
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    ...(isPositive && {
      backgroundColor: '#dcfce7',
      color: '#15803d',
    }),
    ...(isNegative && {
      backgroundColor: '#fee2e2',
      color: '#dc2626',
    }),
    ...(!isPositive && !isNegative && {
      backgroundColor: '#f3f4f6',
      color: '#6b7280',
    }),
  };

  return (
    <span
      style={badgeStyle}
      title={`${perf.gainers} up, ${perf.losers} down of ${perf.stocksWithData} stocks`}
    >
      {changeText}
    </span>
  );
};

// Market pulse indicator showing lists up vs down for a specific category
const MarketPulseIndicator = ({ performanceData, categoryFilters, isSelected }) => {
  const stats = useMemo(() => {
    if (!performanceData || Object.keys(performanceData).length === 0) return null;
    if (!categoryFilters || categoryFilters.length === 0) return null;

    // Get list IDs for this category
    const categoryListIds = new Set(categoryFilters.map(f => f.id));

    let listsUp = 0;
    let listsDown = 0;

    Object.entries(performanceData).forEach(([listId, perf]) => {
      if (categoryListIds.has(listId)) {
        if (perf.avgChange > 0) listsUp++;
        else if (perf.avgChange < 0) listsDown++;
      }
    });

    if (listsUp === 0 && listsDown === 0) return null;
    return { listsUp, listsDown };
  }, [performanceData, categoryFilters]);

  if (!stats) return null;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        padding: '2px 6px',
        backgroundColor: isSelected ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.05)',
        borderRadius: '10px',
        fontSize: '10px',
        fontWeight: 600,
      }}
      title={`${stats.listsUp} lists up, ${stats.listsDown} lists down today`}
    >
      <span style={{ color: isSelected ? '#bbf7d0' : '#16a34a' }}>{stats.listsUp}↑</span>
      <span style={{ color: isSelected ? 'rgba(255,255,255,0.8)' : '#9ca3af' }}>/</span>
      <span style={{ color: isSelected ? '#fecaca' : '#dc2626' }}>{stats.listsDown}↓</span>
    </span>
  );
};

// Shared "section header + expandable rows" chrome for the My Lists dropdown's Labels
// and Strategies sections (issue #337) -- identical header markup (icon, title, count,
// chevron), only the icon/count/rows differ. Keeps the two from drifting apart pixel by
// pixel; row rendering stays with each caller since labels (colour dot, trackPurchases,
// a Manage Labels footer) and strategies (tooltip caption) diverge there.
const CollapsibleFilterSection = ({ icon: Icon, label, count, expanded, onToggle, children }) => (
  <div className="border-t border-gray-200">
    <button onClick={onToggle} className="ai-filter-menu-item group">
      <div className="flex items-center">
        <span className="ai-icon">
          <Icon />
        </span>
        <span className="font-medium">{label}</span>
        <span className="ml-2 text-xs text-gray-400">({count})</span>
      </div>
      <MuiIcons.ExpandMore
        className={`transition-transform duration-200 text-gray-400 group-hover:text-blue-500
          ${expanded ? 'rotate-180' : ''}`}
      />
    </button>
    {expanded && (
      <div className="px-4 py-2 border-t border-gray-100">
        {children}
      </div>
    )}
  </div>
);

const FilterDropdown = ({
  category,
  selectedTab,
  onSelectTab,
  onAddSymbol,
  loggedInUser,
  existingSymbols,
  isOpen,
  onToggle,
  watchlist,
  isListInCategory,
  userLists,
  favoritedSmartLists = [],
  onModalOpen,
  onModalContentSet,
  onSuccess,
  onDeleteList
}) => {
  const containerRef = useRef(null);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const queryClient = useQueryClient();

  // Fetch list performance data
  const { data: performanceData = {} } = useListPerformance();

  // Fetch watchlist labels
  const { data: labelsData = { labels: [], assignments: {} } } = useWatchlistLabels(loggedInUser?.email);

  // Strategies (issue #337): the user's strategies with watch / open counts, from
  // the same query the Strategies widget reads — only the personal category lists them.
  const isPersonalCategory = category.id === 'personal';
  const { data: signalWatchData } = useSignalWatches(isPersonalCategory && !!loggedInUser?.email, loggedInUser?.email);
  const strategies = signalWatchData?.strategies || [];

  useClickOutside(containerRef, { current: popperElement }, () => {
    if (isOpen) {
      onToggle(null);
    }
  });

  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 4], // [x, y] offset
        },
      },
    ],
  });

  const getIconName = useCallback((iconString) => {
    return iconString?.startsWith('fa-') ? iconString.slice(3) : iconString;
  }, []);

  // Extract custom filters separately
  const customFilters = Array.isArray(userLists) 
    ? userLists.filter(list => list.type === 'custom_filter')
        .map(list => ({
          id: list.id,
          name: list.name || 'Unnamed List',
          icon: list.icon || 'FilterAlt',
          description: list.description,
          tooltip: list.tooltip,
          type: list.type,
          symbols: list.symbols || []
        }))
    : [];
  
  console.log('Custom filters:', customFilters);
  
  // This category's smart lists, nested ones included: parents first, then their children, each
  // with its nesting `depth` (the dropdown indents by it).
  const smartFilters = flattenFilters(category.filters).map(({ filter, depth }) => ({ ...filter, depth }));

  const filters = isPersonalCategory
    ? [
        { id: 'watchlist', name: 'Watch List', icon: 'star' },
        { id: 'ai_filter', name: 'Generate New List with AI', icon: 'magic' },
        ...(Array.isArray(userLists) && userLists.length > 0
          ? userLists
              // Filter out custom_filter type lists from the main menu - they'll be shown in the Custom Filters dropdown only
              .filter(list => list.type !== 'custom_filter')
              .map(list => {
                if (!list?.id) {
                  console.warn('Invalid list found:', list);
                  return null;
                }
                return {
                  id: list.id,
                  name: list.name || 'Unnamed List',
                  icon: list.type === 'ai_filter' ? 'magic' : (list.icon || 'list'),
                  description: list.description,
                  tooltip: list.tooltip,
                  type: list.type,
                  symbols: list.symbols || []
                };
              }).filter(Boolean)  // Remove any null entries
          : []),
        // Add favorited smart lists
        ...(favoritedSmartLists || []),
        ...smartFilters
      ]
    : smartFilters;

  console.log('Final filters array:', filters);

  const { data: aiPortfolios = [] } = useAIPortfolios(loggedInUser?.email);
  const { data: powerCheck } = usePowerUserCheck(loggedInUser?.email);
  const isPowerUser = powerCheck?.isPowerUser || false;
  const { data: publicPortfolios = [] } = usePublicPortfolios(loggedInUser?.email);
  const { data: followedPortfolios = [] } = useFollowedPortfolios(loggedInUser?.email);
  const followMutation = useFollowPortfolio();
  const unfollowMutation = useUnfollowPortfolio();
  const dismissMutation = useDismissPortfolio();

  // Check if a label is selected
  const selectedLabel = selectedTab?.startsWith('label_')
    ? labelsData.labels.find(l => l.id === selectedTab.replace('label_', ''))
    : null;

  const selectedPortfolio = selectedTab?.startsWith('ptf_')
    ? aiPortfolios.find(p => p.id === selectedTab)
    : null;

  const selectedStrategy = findStrategyForTab(strategies, selectedTab);

  const selectedFilter = filters.find(f => f.id === selectedTab) ||
                         customFilters.find(f => f.id === selectedTab) ||
                         (selectedLabel ? { id: selectedTab, name: selectedLabel.name, icon: 'LocalOffer' } : null) ||
                         (selectedPortfolio ? { id: selectedTab, name: selectedPortfolio.name, icon: 'SmartToy' } : null) ||
                         (selectedStrategy ? { id: selectedTab, name: selectedStrategy.name, icon: 'Insights' } : null);

  const isSelected = isPersonalCategory
    ? selectedTab === 'watchlist' ||
      selectedTab?.startsWith('label_') ||
      selectedTab?.startsWith('ptf_') ||
      isStrategyTab(selectedTab) ||
      userLists?.some(list => list.id === selectedTab) ||
      favoritedSmartLists?.some(f => f.id === selectedTab) ||
      smartFilters.some(f => f.id === selectedTab)
    : selectedTab === category.id || smartFilters.some(f => f.id === selectedTab);

  const updateDropdownPosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
    }
  }, [isOpen]);

  //console.log('Category prop received:', category);

  const [showAIFilter, setShowAIFilter] = useState(false);

  const handleFilterClick = (filterId, event) => {
    if (filterId === 'ai_filter') {
      journeyTracker.trackAction('ai_list_generate');
      setShowAIFilter(true);
    } else {
      if (selectedTab !== filterId) {
        // Track list selection
        journeyTracker.trackListSelect(filterId, filterId);
        onSelectTab(filterId);
      }
      onToggle(null);
    }
  };

  // Separate AI Filter from regular filters
  const regularFilters = filters.filter(f => f.id !== 'ai_filter');
  const aiFilter = filters.find(f => f.id === 'ai_filter');

  const [isAIFilterExpanded, setIsAIFilterExpanded] = useState(false);
  const [isPortfolioExpanded, setIsPortfolioExpanded] = useState(false);

  // Create a combined handler for success and menu closing
  const handleSuccess = useCallback((savedList) => {
    // Show a notification with the match count if it's available
    if (savedList && savedList.matchCount) {
      // You may need to implement a notification system or adjust this according to your app's notification system
      console.log(`Filter created with ${savedList.matchCount} matching stocks`);
    }
    
    onSuccess?.(savedList);  // Call the parent's onSuccess
    onToggle?.(null);       // Close the menu
  }, [onSuccess, onToggle]);

  const [isCustomFilterExpanded, setIsCustomFilterExpanded] = useState(false);
  const [showCustomFilterModal, setShowCustomFilterModal] = useState(false);
  const [isAIPortfoliosExpanded, setIsAIPortfoliosExpanded] = useState(() => selectedTab?.startsWith('ptf_'));
  const [showCreatePortfolioModal, setShowCreatePortfolioModal] = useState(false);
  const [isSavedListsExpanded, setIsSavedListsExpanded] = useState(false);
  const [isLabelsExpanded, setIsLabelsExpanded] = useState(false);
  const [isStrategiesExpanded, setIsStrategiesExpanded] = useState(() => isStrategyTab(selectedTab));
  const [showLabelManager, setShowLabelManager] = useState(false);

  return (
    <>
      <div className="inline-flex relative" ref={containerRef}>
        <button
          ref={setReferenceElement}
          onClick={() => onToggle(category.id)}
          className={`
            filter-button px-4 py-2 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200
            ${isSelected
              ? 'bg-blue-500 text-white shadow-blue-200' 
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}
          `}
          data-tooltip-id="tooltip"
          data-tooltip-content={category.description}
          data-tooltip-place="top"
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              <IconComponent iconName={category.icon} className="mr-2" />
              <span className="dropdown-text">{category.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <MuiIcons.ExpandMore className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
              <MarketPulseIndicator performanceData={performanceData} categoryFilters={category.filters} isSelected={isSelected} />
            </div>
          </div>
          {isSelected && selectedFilter && (
            <div className="text-sm opacity-75 mt-1 flex items-center">
              <IconComponent iconName={selectedFilter.icon} className="mr-2" />
              <span>{selectedFilter.name}</span>
            </div>
          )}
        </button>

        {isOpen && createPortal(
          <div 
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
            className="w-56 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-50 transform-gpu"
          >
            <div className="py-1">
              {regularFilters
                .filter(filter => filter.id === 'watchlist')
                .map((filter) => (
                  <div
                    key={filter.id}
                    className={`
                      flex items-center justify-between w-full
                      ${selectedTab === filter.id ? 'bg-blue-100' : ''}
                      hover:bg-gradient-to-r from-blue-50 to-white
                      border-l-4 hover:border-blue-500 border-transparent
                      transition-all duration-200
                      group
                    `}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={filter.tooltip || filter.description}
                  >
                    <button
                      onClick={(e) => handleFilterClick(filter.id, e)}
                      className={`
                        flex items-center w-full px-4 py-2 text-sm text-left
                        ${selectedTab === filter.id 
                          ? 'text-blue-700 font-bold' 
                          : 'text-gray-700 font-bold hover:text-blue-600'}
                        transition-colors duration-200
                      `}
                    >
                      <span className="category-icon mr-6 text-blue-500">
                        <IconComponent iconName={filter.icon} />
                      </span>
                      <span>{filter.name}</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onModalContentSet({
                          title: 'Add To Watch List',
                          content: <AddSymbolButton
                            onAddSymbol={onAddSymbol}
                            existingSymbols={existingSymbols}
                            isLoggedIn={!!loggedInUser?.email}
                            className="w-full"
                            usePortal={false}
                          />
                        });
                        onModalOpen(true);
                      }}
                      className="px-3 py-2 text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      title="Add symbol to Watch List"
                    >
                      <IconComponent iconName="plus" />
                    </button>
                  </div>
                ))}

              {category.id === 'personal' && (
                <>
                  {/* Labels Section - Sub-watchlists */}
                  {labelsData.labels.length > 0 && (
                    <CollapsibleFilterSection
                      icon={MuiIcons.LocalOffer}
                      label="Labels"
                      count={labelsData.labels.length}
                      expanded={isLabelsExpanded}
                      onToggle={() => setIsLabelsExpanded(!isLabelsExpanded)}
                    >
                      {labelsData.labels.map((label) => {
                        const stockCount = Object.values(labelsData.assignments || {}).filter(
                          (a) => a.labels?.includes(label.id)
                        ).length;
                        const isSelected = selectedTab === `label_${label.id}`;

                        return (
                          <div
                            key={label.id}
                            className={`
                              group flex items-center justify-between w-full ai-filter-list mt-1
                              ${isSelected ? 'bg-blue-50' : ''}
                            `}
                          >
                            <button
                              onClick={() => {
                                onSelectTab(`label_${label.id}`);
                                onToggle(null);
                              }}
                              className={`
                                flex items-center flex-grow px-3 py-1.5 text-sm text-left
                                ${isSelected ? 'text-blue-700' : 'text-gray-700 hover:bg-gray-50'}
                                rounded
                              `}
                            >
                              <span
                                className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                                style={{ backgroundColor: label.color }}
                              />
                              <span className="flex-grow truncate">{label.name}</span>
                              <span className="text-xs text-gray-400 ml-2">{stockCount}</span>
                              {label.trackPurchases && (
                                <MuiIcons.AttachMoney style={{ fontSize: 14, color: '#10B981', marginLeft: 4 }} />
                              )}
                            </button>
                          </div>
                        );
                      })}
                      <button
                        onClick={() => {
                          onModalContentSet({
                            title: 'Manage Labels',
                            content: (
                              <LabelManagerModal
                                userEmail={loggedInUser?.email}
                                onClose={() => onModalOpen(false)}
                                initialMode="list"
                              />
                            )
                          });
                          onModalOpen(true);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 mt-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <MuiIcons.Settings style={{ fontSize: 16 }} />
                        <span>Manage Labels</span>
                      </button>
                    </CollapsibleFilterSection>
                  )}

                  {/* Create Label button if no labels exist */}
                  {labelsData.labels.length === 0 && loggedInUser?.email && (
                    <div className="border-t border-gray-200">
                      <button
                        onClick={() => {
                          onModalContentSet({
                            title: 'Create Label',
                            content: (
                              <LabelManagerModal
                                userEmail={loggedInUser?.email}
                                onClose={() => onModalOpen(false)}
                                initialMode="create"
                              />
                            )
                          });
                          onModalOpen(true);
                        }}
                        className="ai-filter-menu-item group"
                      >
                        <div className="flex items-center">
                          <span className="ai-icon">
                            <MuiIcons.LocalOffer />
                          </span>
                          <span className="font-medium">Create Label</span>
                        </div>
                        <MuiIcons.Add className="text-gray-400 group-hover:text-blue-500" />
                      </button>
                    </div>
                  )}

                  {/* Strategies Section (issue #337) - each strategy's opted-in stocks as a list */}
                  {strategies.length > 0 && (
                    <CollapsibleFilterSection
                      icon={MuiIcons.Insights}
                      label="Strategies"
                      count={strategies.length}
                      expanded={isStrategiesExpanded}
                      onToggle={() => setIsStrategiesExpanded(!isStrategiesExpanded)}
                    >
                      {strategies.map((strategy) => {
                        const tabId = strategyTabId(strategy.id);
                        const isStrategySelected = selectedTab === tabId;
                        return (
                          <div
                            key={strategy.id}
                            className={`
                              group flex items-center justify-between w-full ai-filter-list mt-1
                              ${isStrategySelected ? 'bg-blue-50' : ''}
                            `}
                            data-tooltip-id="tooltip"
                            data-tooltip-content={strategyCaption(strategy)}
                          >
                            <button
                              onClick={() => {
                                onSelectTab(tabId);
                                onToggle(null);
                              }}
                              className={`
                                flex items-center flex-grow px-3 py-1.5 text-sm text-left
                                ${isStrategySelected ? 'text-blue-700' : 'text-gray-700 hover:bg-gray-50'}
                                rounded
                              `}
                            >
                              <span className="flex-grow truncate">{strategy.name}</span>
                              <span className="text-xs text-gray-400 ml-2" style={{ whiteSpace: 'nowrap' }}>
                                {strategy.watchCount ?? 0} · {strategy.openPositions ?? 0}/{strategy.positionCap ?? '?'} open
                              </span>
                            </button>
                          </div>
                        );
                      })}
                    </CollapsibleFilterSection>
                  )}

                  {/* Favorite Lists Section - Favorited Smart Lists */}
                  {regularFilters.filter(f => f.type === 'favorite_smart_list').length > 0 && (
                    <div className="border-t border-gray-200">
                      <button
                        onClick={() => setIsSavedListsExpanded(!isSavedListsExpanded)}
                        className="ai-filter-menu-item group"
                      >
                        <div className="flex items-center">
                          <span className="ai-icon">
                            <MuiIcons.BookmarkBorder />
                          </span>
                          <span className="font-medium">Favorite Lists</span>
                        </div>
                        <MuiIcons.ExpandMore
                          className={`transition-transform duration-200 text-gray-400 group-hover:text-blue-500
                            ${isSavedListsExpanded ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {isSavedListsExpanded && (
                        <div className="px-4 py-2 border-t border-gray-100">
                          {regularFilters
                            .filter(filter => filter.type === 'favorite_smart_list')
                            .map((filter) => (
                              <div
                                key={filter.id}
                                className={`
                                  group flex items-center justify-between w-full ai-filter-list mt-1
                                  ${selectedTab === filter.id ? 'bg-blue-50' : ''}
                                `}
                                data-tooltip-id="tooltip"
                                data-tooltip-content={`${filter.description || filter.name} (from ${filter.sourceCategory})`}
                              >
                                <button
                                  onClick={(e) => handleFilterClick(filter.id, e)}
                                  className={`
                                    flex items-center flex-grow px-3 py-1.5 text-sm text-left
                                    ${selectedTab === filter.id ? 'text-blue-700' : 'text-gray-700 hover:bg-gray-50'}
                                    rounded
                                  `}
                                >
                                  <span className="category-icon mr-2">
                                    <IconComponent iconName={filter.icon} />
                                  </span>
                                  <span className="flex-grow">{filter.name}</span>
                                  <PerformanceBadge listId={filter.id} performanceData={performanceData} />
                                  <SubscriptionIndicator listId={filter.id} userEmail={loggedInUser?.email} />
                                </button>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await toggleFavoriteList(filter.id, false);
                                    queryClient.invalidateQueries(['favoriteLists', loggedInUser?.email]);
                                  }}
                                  className="px-2 py-1.5 text-gray-400 hover:text-red-500"
                                  title="Remove from My Lists"
                                >
                                  <MuiIcons.Close style={{ fontSize: 16 }} />
                                </button>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="border-y border-gray-200">
                    <button
                      onClick={() => setIsAIFilterExpanded(!isAIFilterExpanded)}
                      className="ai-filter-menu-item group"
                    >
                      <div className="flex items-center">
                        <span className="ai-icon">
                          <IconComponent iconName="magic" />
                        </span>
                        <span className="font-medium">Generate list with AI</span>
                      </div>
                      <MuiIcons.ExpandMore
                        className={`transition-transform duration-200 text-gray-400 group-hover:text-blue-500
                          ${isAIFilterExpanded ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isAIFilterExpanded && (
                      <div className="px-4 py-2 border-t border-gray-100">
                        <AIFilterModal 
                          onClose={() => onToggle(null)}
                          onSuccess={handleSuccess}
                          loggedInUser={loggedInUser}
                          mode="filter"
                        />
                        
                        {/* Display AI-generated lists */}
                        {regularFilters
                          .filter(filter => filter.type === 'ai_filter')
                          .map((filter) => (
                            <div
                              key={filter.id}
                              className={`
                                flex items-center justify-between w-full ai-filter-list mt-2
                                ${selectedTab === filter.id ? 'bg-blue-50' : ''}
                              `}
                              data-tooltip-id="tooltip"
                              data-tooltip-content={filter.tooltip || filter.description}
                            >
                              <button
                                onClick={(e) => handleFilterClick(filter.id, e)}
                                className={`
                                  flex items-center flex-grow px-3 py-1.5 text-sm text-left
                                  ${selectedTab === filter.id ? 'text-blue-700' : 'text-gray-700 hover:bg-gray-50'}
                                  rounded
                                `}
                              >
                                <span className="category-icon mr-2">
                                  <IconComponent iconName={filter.icon} />
                                </span>
                                <span className="flex-grow">{filter.name}</span>
                                <PerformanceBadge listId={filter.id} performanceData={performanceData} />
                                <SubscriptionIndicator listId={filter.id} userEmail={loggedInUser?.email} />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteList(filter.id);
                                }}
                                className="px-2 py-1.5 text-gray-400 hover:text-red-500"
                                title="Delete list"
                              >
                                <IconComponent iconName="trash" />
                              </button>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* New Custom Filters Section */}
                  <div className="border-b border-gray-200">
                    <button
                      onClick={() => setIsCustomFilterExpanded(!isCustomFilterExpanded)}
                      className="ai-filter-menu-item group"
                    >
                      <div className="flex items-center">
                        <span className="ai-icon">
                          <IconComponent iconName="FilterAlt" />
                        </span>
                        <span className="font-medium">Custom Filters</span>
                      </div>
                      <MuiIcons.ExpandMore 
                        className={`transition-transform duration-200 text-gray-400 group-hover:text-blue-500
                          ${isCustomFilterExpanded ? 'rotate-180' : ''}`}
                      />
                    </button>
                    
                    {isCustomFilterExpanded && (
                      <div className="px-4 py-2 border-t border-gray-100">
                        <button
                          onClick={() => setShowCustomFilterModal(true)}
                          className="w-full px-3 py-2 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors duration-200 flex items-center justify-center gap-2 mb-3"
                        >
                          <IconComponent iconName="add" />
                          <span>Create New Custom Filter</span>
                        </button>
                        
                        {/* Display custom filter lists */}
                        {customFilters.map((filter) => (
                          <div
                            key={filter.id}
                            className={`
                              group flex items-center justify-between w-full ai-filter-list mt-2
                              ${selectedTab === filter.id ? 'bg-blue-50' : ''}
                            `}
                            data-tooltip-id="tooltip"
                            data-tooltip-content={filter.tooltip || filter.description}
                          >
                            <button
                              onClick={(e) => handleFilterClick(filter.id, e)}
                              className={`
                                flex items-center flex-grow px-3 py-1.5 text-sm text-left
                                ${selectedTab === filter.id ? 'text-blue-700' : 'text-gray-700 hover:bg-gray-50'}
                                rounded
                              `}
                            >
                              <span className="category-icon mr-2">
                                <IconComponent iconName={filter.icon} />
                              </span>
                              <span className="flex-grow">{filter.name}</span>
                              <PerformanceBadge listId={filter.id} performanceData={performanceData} />
                              <SubscriptionIndicator listId={filter.id} userEmail={loggedInUser?.email} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteList(filter.id);
                              }}
                              className="px-2 py-1.5 text-gray-400 hover:text-red-500"
                              title="Delete filter"
                            >
                              <IconComponent iconName="trash" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* AI Portfolios Section */}
                  <div className="border-b border-gray-200">
                    <button
                      onClick={() => setIsAIPortfoliosExpanded(!isAIPortfoliosExpanded)}
                      className="ai-filter-menu-item group"
                    >
                      <div className="flex items-center">
                        <span className="ai-icon">
                          <IconComponent iconName="SmartToy" />
                        </span>
                        <span className="font-medium">AI Managed Portfolios</span>
                      </div>
                      <MuiIcons.ExpandMore
                        className={`transition-transform duration-200 text-gray-400 group-hover:text-blue-500
                          ${isAIPortfoliosExpanded ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {isAIPortfoliosExpanded && (
                      <div className="px-4 py-2 border-t border-gray-100">
                        {isPowerUser && (
                          <button
                            onClick={() => { console.log('[AI-PORTFOLIO] Create button clicked'); setShowCreatePortfolioModal(true); }}
                            className="w-full px-3 py-2 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors duration-200 flex items-center justify-center gap-2 mb-3"
                          >
                            <IconComponent iconName="add" />
                            <span>Create AI Managed Portfolio</span>
                          </button>
                        )}

                        {aiPortfolios.map((portfolio) => {
                          const tradesToday = getPortfolioTradesToday(portfolio);
                          const isPaused = portfolio.status !== 'active';
                          return (
                            <div
                              key={portfolio.id}
                              className={`
                                group flex items-center justify-between w-full ai-filter-list mt-2
                                ${selectedTab === portfolio.id ? 'bg-blue-50' : ''}
                              `}
                            >
                              <button
                                onClick={(e) => handleFilterClick(portfolio.id, e)}
                                className={`
                                  flex items-center flex-grow px-3 py-1.5 text-sm text-left
                                  ${selectedTab === portfolio.id ? 'text-blue-700' : isPaused ? 'text-gray-400' : 'text-gray-700 hover:bg-gray-50'}
                                  rounded
                                `}
                              >
                                <span className={`category-icon mr-2 ${isPaused ? 'opacity-40' : ''}`}>
                                  <MuiIcons.AccountBalance fontSize="small" />
                                </span>
                                <span className={`flex-grow ${isPaused ? 'line-through decoration-gray-300' : ''}`}>{portfolio.name}</span>
                                {!isPaused && <PortfolioTodayBadge portfolioId={portfolio.id} />}
                                {tradesToday > 0 ? (
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '6px',
                                    fontSize: '11px', fontWeight: 600, color: '#15803d', whiteSpace: 'nowrap',
                                  }}>
                                    <span className="ptf-pulse-dot" />
                                    {tradesToday}
                                  </span>
                                ) : isPaused ? (
                                  <span className="text-xs ml-2 text-gray-300">⏸</span>
                                ) : null}
                              </button>
                            </div>
                          );
                        })}

                        {/* Followed Portfolios */}
                        {followedPortfolios.length > 0 && (
                          <div className="mt-3 pt-2 border-t border-gray-100">
                            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide px-1 mb-1">Following</div>
                            {followedPortfolios.map((portfolio) => (
                              <div
                                key={portfolio.id}
                                className={`group flex items-center justify-between w-full ai-filter-list mt-1 ${selectedTab === portfolio.id ? 'bg-blue-50' : ''}`}
                              >
                                <button
                                  onClick={(e) => handleFilterClick(portfolio.id, e)}
                                  className={`flex items-center flex-grow px-3 py-1.5 text-sm text-left ${selectedTab === portfolio.id ? 'text-blue-700' : 'text-gray-700 hover:bg-gray-50'} rounded`}
                                >
                                  <span className="category-icon mr-2 opacity-60">
                                    <MuiIcons.Visibility fontSize="small" />
                                  </span>
                                  <span className="flex-grow truncate">{portfolio.name}</span>
                                  <PortfolioTodayBadge portfolioId={portfolio.id} />
                                </button>
                                <button
                                  onClick={() => {
                                    unfollowMutation.mutate(
                                      { email: loggedInUser.email, portfolioId: portfolio.id },
                                      { onSuccess: () => {
                                        queryClient.invalidateQueries({ queryKey: ['followedPortfolios'] });
                                        queryClient.invalidateQueries({ queryKey: ['publicPortfolios'] });
                                      }}
                                    );
                                  }}
                                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity"
                                  title="Unfollow"
                                >
                                  <MuiIcons.Close style={{ fontSize: 14 }} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Public Portfolios Discovery */}
                        {publicPortfolios.filter(p => !followedPortfolios.some(f => f.id === p.id)).length > 0 && (
                          <div className={`${aiPortfolios.length > 0 || followedPortfolios.length > 0 ? 'mt-3 pt-2 border-t border-gray-100' : ''}`}>
                            <div className="text-xs font-medium text-gray-400 uppercase tracking-wide px-1 mb-1">Public Portfolios</div>
                            {publicPortfolios
                              .filter(p => !followedPortfolios.some(f => f.id === p.id))
                              .map((portfolio) => (
                              <div
                                key={portfolio.id}
                                className={`group flex items-center justify-between w-full ai-filter-list mt-1 ${selectedTab === portfolio.id ? 'bg-blue-50' : ''}`}
                              >
                                <button
                                  onClick={(e) => handleFilterClick(portfolio.id, e)}
                                  className="flex items-center flex-grow px-3 py-1.5 text-sm text-left text-gray-600 hover:bg-gray-50 rounded"
                                >
                                  <span className="category-icon mr-2 opacity-40">
                                    <MuiIcons.Public fontSize="small" />
                                  </span>
                                  <span className="flex-grow truncate">{portfolio.name}</span>
                                  <PortfolioTodayBadge portfolioId={portfolio.id} />
                                </button>
                                {loggedInUser?.email && (
                                  <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity">
                                    <button
                                      onClick={() => {
                                        followMutation.mutate(
                                          { email: loggedInUser.email, portfolioId: portfolio.id },
                                          { onSuccess: () => {
                                            queryClient.invalidateQueries({ queryKey: ['followedPortfolios'] });
                                            queryClient.invalidateQueries({ queryKey: ['publicPortfolios'] });
                                          }}
                                        );
                                      }}
                                      className="p-1 text-blue-500 hover:text-blue-700"
                                      title="Follow this portfolio"
                                    >
                                      <MuiIcons.Add style={{ fontSize: 14 }} />
                                    </button>
                                    <button
                                      onClick={() => {
                                        dismissMutation.mutate(
                                          { email: loggedInUser.email, portfolioId: portfolio.id },
                                          { onSuccess: () => {
                                            queryClient.invalidateQueries({ queryKey: ['publicPortfolios'] });
                                          }}
                                        );
                                      }}
                                      className="p-1 text-gray-400 hover:text-red-500"
                                      title="Not interested"
                                    >
                                      <MuiIcons.Close style={{ fontSize: 14 }} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="border-b border-gray-200">
                    <button
                      onClick={() => setIsPortfolioExpanded(!isPortfolioExpanded)}
                      className="ai-filter-menu-item group"
                    >
                      <div className="flex items-center">
                        <span className="ai-icon">
                          <IconComponent iconName="analytics" />
                        </span>
                        <span className="font-medium">Analyze Portfolio</span>
                      </div>
                      <MuiIcons.ExpandMore 
                        className={`transition-transform duration-200 text-gray-400 group-hover:text-blue-500
                          ${isPortfolioExpanded ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isPortfolioExpanded && (
                      <div className="px-4 py-2 border-t border-gray-100">
                        <AIFilterModal 
                          onClose={() => onToggle(null)}
                          onSuccess={handleSuccess}
                          loggedInUser={loggedInUser}
                          mode="portfolio"
                        />
                        
                        {/* Display portfolio analysis results */}
                        {regularFilters
                          .filter(filter => filter.type === 'portfolio')
                          .map((filter) => (
                            <div
                              key={filter.id}
                              className={`
                                flex items-center justify-between w-full ai-filter-list mt-2
                                ${selectedTab === filter.id ? 'bg-blue-50' : ''}
                              `}
                              data-tooltip-id="tooltip"
                              data-tooltip-content={filter.tooltip || filter.description}
                            >
                              <button
                                onClick={(e) => handleFilterClick(filter.id, e)}
                                className={`
                                  flex items-center flex-grow px-3 py-1.5 text-sm text-left
                                  ${selectedTab === filter.id ? 'text-blue-700' : 'text-gray-700 hover:bg-gray-50'}
                                  rounded
                                `}
                              >
                                <span className="category-icon mr-2">
                                  <IconComponent iconName={filter.icon} />
                                </span>
                                <span>{filter.name}</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteList(filter.id);
                                }}
                                className="px-2 py-1.5 text-gray-400 hover:text-red-500"
                                title="Delete list"
                              >
                                <IconComponent iconName="trash" />
                              </button>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </>
              )}
              
              {regularFilters
                .filter(filter => filter.id !== 'watchlist' && filter.type !== 'ai_filter' && filter.type !== 'portfolio' && filter.type !== 'favorite_smart_list')
                .map((filter) => (
                  <div
                    key={filter.id}
                    className={`
                      flex items-center justify-between w-full
                      ${(filter.type === 'ai_filter' || filter.type === 'portfolio') ? 'ai-filter-list' : 'dropdown-menu-item'}
                      ${selectedTab === filter.id ? 'bg-blue-50' : ''}
                    `}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={filter.tooltip || filter.description}
                  >
                    {/* a nested list sits under its parent, indented 14px per level (px-4 is 16px) */}
                    <button
                      onClick={(e) => handleFilterClick(filter.id, e)}
                      className={`
                        flex items-center flex-grow px-4 py-2 text-sm text-left
                        ${selectedTab === filter.id ? 'text-blue-700' : 'text-gray-700 hover:bg-gray-50'}
                      `}
                      style={filter.depth ? { paddingLeft: 16 + filter.depth * 14 } : undefined}
                    >
                      <span className="category-icon mr-6">
                        <IconComponent iconName={filter.icon} />
                      </span>
                      <span>{filter.name}</span>
                      <PerformanceBadge listId={filter.id} performanceData={performanceData} />
                      <SubscriptionIndicator listId={filter.id} userEmail={loggedInUser?.email} />
                    </button>
                    {(filter.type === 'ai_filter' || filter.type === 'portfolio') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteList(filter.id);
                        }}
                        className="px-3 py-2 text-gray-400 hover:text-red-500"
                        title="Delete list"
                      >
                        <IconComponent iconName="trash" />
                      </button>
                    )}
                  </div>
                ))}

            </div>
          </div>,
          document.body
        )}
      </div>
      
      {/* Add the custom filter modal */}
      {showCustomFilterModal && (
        <CustomFilterModal
          isOpen={showCustomFilterModal}
          onClose={() => setShowCustomFilterModal(false)}
          onSuccess={handleSuccess}
          loggedInUser={loggedInUser}
        />
      )}
      {showCreatePortfolioModal && (
        <AIPortfolioCreateModal
          isOpen={showCreatePortfolioModal}
          onClose={() => setShowCreatePortfolioModal(false)}
          onSuccess={(portfolio) => {
            onSelectTab(portfolio.id);
            onToggle?.(null);
          }}
          loggedInUser={loggedInUser}
        />
      )}
    </>
  );
};

FilterDropdowns.propTypes = {
  selectedTab: PropTypes.string,
  onSelectTab: PropTypes.func.isRequired,
  onAddSymbol: PropTypes.func.isRequired,
  loggedInUser: PropTypes.shape({
    email: PropTypes.string.isRequired,
    name: PropTypes.string
  }),
  existingSymbols: PropTypes.array,
  activeFilters: PropTypes.object,
  onFilterApply: PropTypes.func.isRequired,
  watchlist: PropTypes.array,
  userSettings: PropTypes.object,
  onModalOpen: PropTypes.func.isRequired,
  onModalContentSet: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onDeleteList: PropTypes.func.isRequired
};

FilterDropdown.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    icon: PropTypes.string,
    filters: PropTypes.array,
    description: PropTypes.string
  }).isRequired,
  selectedTab: PropTypes.string,
  onSelectTab: PropTypes.func.isRequired,
  onAddSymbol: PropTypes.func,
  loggedInUser: PropTypes.shape({
    email: PropTypes.string.isRequired,
    name: PropTypes.string
  }),
  existingSymbols: PropTypes.array,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  watchlist: PropTypes.array,
  userLists: PropTypes.array,
  favoritedSmartLists: PropTypes.array,
  onModalOpen: PropTypes.func.isRequired,
  onModalContentSet: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onDeleteList: PropTypes.func.isRequired
};

export default FilterDropdowns; 